import React from 'react';
import { observer } from 'mobx-react-lite';
import { Dropdown } from 'primereact/dropdown';
import DataModal from '../DataModal/DataModal';
import ContentSectionSkeleton from '../SectionSkeletons/ContentSectionSkeleton';
import LossOfExclusivityDetailModal from '../LossOfExclusivitySection/components/LossOfExclusivityDetailModal/LossOfExclusivityDetailModal';
import ContractExpirationDetailModal from './components/ContractExpirationDetailModal/ContractExpirationDetailModal';
import SolicitationDetailModal from '../SolicitationDetailModal/SolicitationDetailModal';
import { ANDADetailModal } from 'components/shared/ANDADetailModal';
import './ContractExpirationsSection.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { ContractExpirationsSectionProps } from './ContractExpirationsSection.type';
import { useContractExpirationsSection } from './useContractExpirationsSection.logic';
import ContractExpirationCard from './components/ContractExpirationCard/ContractExpirationCard';

const ContractExpirationsSection: React.FC<ContractExpirationsSectionProps> = observer(
  ({ contracts }) => {
    const {
      showModal,
      setShowModal,
      currentSection,
      setCurrentSection,
      columns,
      modalData,
      handlePeriodChange,
      handleAndaPeriodChange,
      handleLoePeriodChange,
      getCurrentSectionLoading,
      handleSectionChange,
      selectedContractExpirationsPeriod,
      selectedAndaUpdatesPeriod,
      selectedLossOfExclusivityPeriod,
      periodOptions,
      andaPeriodOptions,
      displayedContracts,
      hasMoreContracts,
      isContractExpirationsLoading,
      // Contract expiration detail modal state and handlers
      showContractDetailModal,
      setShowContractDetailModal,
      selectedContract,
      setSelectedContract,
      handleContractCardClick,
      // LOE detail modal state and handlers
      showLoeDetailModal,
      setShowLoeDetailModal,
      selectedLoeCard,
      setSelectedLoeCard,
      // ANDA detail modal state and handlers
      showAndaDetailModal,
      setShowAndaDetailModal,
      selectedAnda,
      setSelectedAnda,
      // Solicitation detail modal state and handlers
      showSolicitationDetailModal,
      setShowSolicitationDetailModal,
      solicitationDetails,
      setSolicitationDetails,
      isSolicitationDetailsLoading,
      // Combined row click handler
      handleRowClick,
      // Filter state for modal highlighting
      conceptFilterTerm,
      ndcFilterTerm,
      handleFiltersChange,
    } = useContractExpirationsSection({ contracts });

    return (
      <>
        <section className="content-section">
          <div className="section-header">
            <div className="section-title">
              <i className="fas fa-calendar-times" style={{ color: 'var(--warning-color)' }}></i>
              Contract Expirations
              <span className="section-badge">{contracts.length} Upcoming</span>
            </div>
            <div className="period-selector">
              <Dropdown
                value={selectedContractExpirationsPeriod}
                options={periodOptions}
                onChange={handlePeriodChange}
                placeholder="Select Period"
                className="period-dropdown"
                appendTo={typeof document !== 'undefined' ? document.body : undefined}
              />
            </div>
          </div>
          <div className="section-content">
            {isContractExpirationsLoading ? (
              <ContentSectionSkeleton />
            ) : contracts.length === 0 ? (
              <div className="no-data-message">No data available for Contract Expirations</div>
            ) : (
              <>
                <div className="three-card-grid">
                  {displayedContracts.map((contract, index) => (
                    <div key={index} className="three-card-wrapper">
                      <ContractExpirationCard
                        title={contract.contract_number}
                        date={contract.date}
                        contract_number={contract.contract_number}
                        contract_owner={contract.contract_owner}
                        agency={contract.agency}
                        contract_type={contract.contract_type}
                        ndcs={contract.ndcs}
                        tags={contract.tags}
                        priority={contract.priority}
                        url={contract.url}
                        onCardClick={() => handleContractCardClick(contract)}
                      />
                    </div>
                  ))}
                </div>
                {hasMoreContracts && (
                  <div className="see-more-container">
                    <button className="see-more-btn" onClick={() => setShowModal(true)}>
                      See More
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <DataModal
          visible={showModal}
          onHide={() => {
            setShowModal(false);
            setCurrentSection('contractExpirations'); // Reset to default section when closing
          }}
          data={modalData}
          sectionType={currentSection}
          columns={columns}
          onSectionChange={handleSectionChange}
          sectionDropdownConfigs={{
            contractExpirations: {
              value: selectedContractExpirationsPeriod,
              options: periodOptions,
              onChange: handlePeriodChange,
              placeholder: 'Select Period',
            },
            andaUpdates: {
              value: selectedAndaUpdatesPeriod,
              options: andaPeriodOptions,
              onChange: handleAndaPeriodChange,
              placeholder: 'Select Period',
            },
            lossOfExclusivity: {
              value: selectedLossOfExclusivityPeriod,
              options: periodOptions,
              onChange: handleLoePeriodChange,
              placeholder: 'Select Period',
            },
          }}
          isLoading={getCurrentSectionLoading()}
          onFiltersChange={handleFiltersChange}
          onRowClick={
            currentSection === 'contractExpirations' ||
            currentSection === 'andaUpdates' ||
            currentSection === 'lossOfExclusivity' ||
            currentSection === 'preSolicitationAlerts'
              ? handleRowClick
              : undefined
          }
        />

        {/* LOE Detail Modal - for when user switches to LOE section */}
        <LossOfExclusivityDetailModal
          visible={showLoeDetailModal}
          onHide={() => {
            setShowLoeDetailModal(false);
            setSelectedLoeCard(null);
          }}
          data={selectedLoeCard}
        />

        {/* ANDA Detail Modal - for when user switches to ANDA section */}
        <ANDADetailModal
          visible={showAndaDetailModal}
          onHide={() => {
            setShowAndaDetailModal(false);
            setSelectedAnda(null);
          }}
          data={selectedAnda}
        />

        {/* Solicitation Detail Modal - for when user switches to Solicitation Alerts section */}
        <SolicitationDetailModal
          visible={showSolicitationDetailModal}
          onHide={() => {
            setShowSolicitationDetailModal(false);
            setSolicitationDetails(null);
          }}
          data={solicitationDetails}
          isLoading={isSolicitationDetailsLoading}
        />

        {/* Contract Expiration Detail Modal - card view and table row click */}
        <ContractExpirationDetailModal
          visible={showContractDetailModal}
          onHide={() => {
            setShowContractDetailModal(false);
            setSelectedContract(null);
          }}
          data={selectedContract}
          highlightTerm={conceptFilterTerm}
          ndcHighlightTerm={ndcFilterTerm}
        />
      </>
    );
  }
);

export default ContractExpirationsSection;
